import { last } from "lodash";

import { logError, logEvent } from "../analytics";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 500;

export async function downloadDocument(baseUrl: string, name: string) {
  if (!baseUrl) {
    return;
  }

  const url = baseUrl.replace(/^http:\/\//i, "https://");
  const response = await fetchWithRetry({ url, retries: MAX_RETRIES, delayMs: RETRY_DELAY_MS });

  const file = await response.blob();
  // eslint-disable-next-line unicorn/prefer-global-this
  const source = window.URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = source;
  const urlArray = file.type.toLowerCase().split("/");
  const extension = last(urlArray);

  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  link.setAttribute("download", `${name}.${extension}`);
  document.body.append(link);
  link.click();
}

function isRetryableError(error: unknown): boolean {
  // Only retry network failures (status_code: 0 in RUM), not HTTP 4xx/5xx
  if (error instanceof TypeError) {
    return true;
  }

  if (error instanceof Error && error.message === "Failed to fetch") {
    return true;
  }

  return false;
}

async function fetchWithRetry(params: {
  url: string;
  retries: number;
  delayMs: number;
}): Promise<Response> {
  const { url, retries, delayMs } = params;
  let urlPath: string;
  try {
    urlPath = new URL(url).pathname;
  } catch {
    urlPath = url;
  }

  const logContext = { urlPath, maxRetries: retries };

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Using fetch with cache: "no-store" to bypass the browser cache.
      // This avoids a CORS error that occurs when the same URL was previously
      // loaded by an <img> tag (no-cors mode), causing the browser to serve
      // a cached response without CORS headers for this cors-mode request.
      const response = await fetch(url, { mode: "cors", cache: "no-store" });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      if (attempt > 0) {
        logEvent("App V2 Document download succeeded after retry", {
          ...logContext,
          attempt,
        });
      }

      return response;
    } catch (error: unknown) {
      const isLastAttempt = attempt >= retries;

      if (isLastAttempt || !isRetryableError(error)) {
        logError("App V2 Document download failed", {
          error,
          metadata: { ...logContext, attempt, retryable: isRetryableError(error) },
        });
        throw error;
      }

      logEvent("App V2 Document download retrying", {
        ...logContext,
        attempt,
        error: error instanceof Error ? error.message : String(error),
      });

      await new Promise((resolve) => {
        setTimeout(resolve, delayMs);
      });
    }
  }

  // Unreachable, but TypeScript needs it
  throw new Error("Unexpected: exhausted retries without throwing");
}
